/**
 * Head of Department (HOD) → class scope helpers.
 *
 * HOD roles map to all class numbers (4/5/6) of the matching programme:
 *   hod_cmt → CMT4, CMT5, CMT6
 *   hod_pst → PST4, PST5, PST6
 *   hod_sw  → SW4,  SW5,  SW6
 *
 * Mirrors `src/lib/crClassMapping.ts` but at department level.
 */

export const HOD_ROLE_NAMES = ["hod_cmt", "hod_pst", "hod_sw"] as const;
export type HodRole = (typeof HOD_ROLE_NAMES)[number];

/** Roles that override the HOD scope (can see/manage every class). */
const HOD_BYPASS_ROLES = new Set([
  "superadmin",
  "head_academic",
  "head_administration",
  "it_incharge",
  "principal",
  "deputy_principal",
]);

const HOD_PROGRAM_MAP: Record<HodRole, string> = {
  hod_cmt: "CMT",
  hod_pst: "PST",
  hod_sw: "SW",
};

/** Convert an HOD role like `hod_pst` to its programme prefix `PST`. */
export function hodRoleToProgram(role: string): string | null {
  return (HOD_PROGRAM_MAP as Record<string, string>)[role] ?? null;
}

/** Returns the unique class labels (e.g. `["PST4","PST5","PST6"]`) the HOD covers. */
export function getHodAllowedClasses(roles: string[]): string[] {
  const out = new Set<string>();
  for (const r of roles ?? []) {
    const prog = hodRoleToProgram(r);
    if (!prog) continue;
    [4, 5, 6].forEach((n) => out.add(`${prog}${n}`));
  }
  return Array.from(out);
}

/** Returns the programme prefixes (CMT/PST/SW) the user is HOD of. */
export function getHodProgramPrefixes(roles: string[]): string[] {
  const out = new Set<string>();
  for (const r of roles ?? []) {
    const prog = hodRoleToProgram(r);
    if (prog) out.add(prog);
  }
  return Array.from(out);
}

/** True when the user holds an HOD role and NO bypass (head/admin/IT) role. */
export function isHodOnly(roles: string[]): boolean {
  if (!roles || roles.length === 0) return false;
  const hasHod = roles.some((r) => HOD_PROGRAM_MAP[r as HodRole] !== undefined);
  if (!hasHod) return false;
  return !roles.some((r) => HOD_BYPASS_ROLES.has(r));
}

/** True when the user holds any HOD role (regardless of overrides). */
export function hasAnyHodRole(roles: string[]): boolean {
  return (roles ?? []).some((r) => HOD_PROGRAM_MAP[r as HodRole] !== undefined);
}

/** Normalise a class label (uppercase, trimmed). */
export function normaliseClass(cls: string | null | undefined): string {
  return (cls ?? "").trim().toUpperCase();
}

/** True if the given class label belongs to the user's HOD scope. */
export function isClassInHodScope(roles: string[], cls: string | null | undefined): boolean {
  const allowed = new Set(getHodAllowedClasses(roles).map(normaliseClass));
  return allowed.has(normaliseClass(cls));
}
